/**
 * What the platform knows about the customer: every leaf question across every
 * capability, split into the ones it has answered and the ones still open.
 *
 * Two markers sit on open questions. "Asking next" is the one the briefing asked
 * the agent to chase. "Answers conflict" is a question the customer has answered
 * two ways, which is why it went back to being open.
 */
import { useMemo } from "react";
import { Outcome } from "@financial-advisory/data-access/api-types";
import { UnlikelyBadge } from "@targeted-support/ui/features/overview";
import { AlertTriangle, Check, HelpCircle, UserCheck, X } from "lucide-react";
import { cn, normaliseString } from "@unlikelyai-magic/utils";
import type { ChangedNode, QuestionAnswer } from "@unlikelyai-magic/utils";
import { CollapsibleCard } from "./CollapsibleCard";

interface KnownItem {
  question: string;
  value: Outcome;
  reasoning?: string;
}

interface UnknownItem {
  question: string;
  isAskingNext: boolean;
  hasConflict: boolean;
}

interface UserKnowledgeSectionProps {
  questionAnswers: QuestionAnswer[];
  allNodes: Map<string, ChangedNode>;
  isExpanded: boolean;
  onToggle: () => void;
  /**
   * The question the briefing asked the agent to chase this turn.
   */
  askingNext?: string;
  /**
   * Questions the customer has answered two ways. Empty on conversations
   * recorded before the API reported them.
   */
  contradictingQuestions?: string[];
  /**
   * Called when an answered question is clicked, to scroll the chat to the turn
   * that produced it.
   */
  onSelectKnowledge?: (question: string) => void;
}

export function UserKnowledgeSection({
  questionAnswers,
  allNodes,
  isExpanded,
  onToggle,
  askingNext,
  contradictingQuestions = [],
  onSelectKnowledge,
}: UserKnowledgeSectionProps) {
  const { known, unknown } = useMemo(() => {
    const known: KnownItem[] = [];
    const unknown: UnknownItem[] = [];

    const qaMap = new Map(
      questionAnswers.map((qa) => [normaliseString(qa.question), qa])
    );
    const asking = askingNext ? normaliseString(askingNext) : null;
    const conflicting = new Set(contradictingQuestions.map(normaliseString));

    for (const node of allNodes.values()) {
      const key = normaliseString(node.question);
      const qa = qaMap.get(key);
      const value = qa?.value ?? Outcome.UNDECIDED;

      if (value === Outcome.TRUE || value === Outcome.FALSE) {
        known.push({
          question: node.question,
          value,
          reasoning: qa?.reasoning ?? node.reasoning,
        });
      } else {
        unknown.push({
          question: node.question,
          isAskingNext: asking !== null && key === asking,
          hasConflict: conflicting.has(key),
        });
      }
    }

    return { known, unknown };
  }, [questionAnswers, allNodes, askingNext, contradictingQuestions]);

  if (known.length + unknown.length === 0) return null;

  return (
    <CollapsibleCard
      icon={UserCheck}
      title="What we know about this customer"
      badge={<UnlikelyBadge />}
      grow={true}
      isExpanded={isExpanded}
      onToggle={onToggle}
      bodyClassName="gap-3"
      meta={
        <>
          <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[11px] font-medium text-green-600">
            {known.length} known
          </span>
          <span className="rounded-full bg-yellow-500/[0.12] px-2 py-0.5 text-[11px] font-medium text-amber-700">
            {unknown.length} unknown
          </span>
        </>
      }
    >
      {known.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand-muted">
            Known
          </h3>
          {known.map((item) => {
            const isTrue = item.value === Outcome.TRUE;
            const Icon = isTrue ? Check : X;
            return (
              <button
                type="button"
                key={item.question}
                onClick={() => onSelectKnowledge?.(item.question)}
                disabled={!onSelectKnowledge}
                className={cn(
                  "flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left",
                  isTrue
                    ? "border-green-500/30 bg-green-500/[0.05]"
                    : "border-red-500/25 bg-red-500/[0.04]",
                  onSelectKnowledge &&
                    "transition-shadow hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40"
                )}
              >
                <Icon
                  className={cn(
                    "h-3.5 w-3.5 flex-none stroke-[3]",
                    isTrue ? "text-green-600" : "text-red-600"
                  )}
                  aria-hidden={true}
                />
                <span className="text-xs">{item.question}</span>
                {item.reasoning && (
                  <span className="min-w-0 truncate text-[11px] text-brand-muted">
                    {item.reasoning}
                  </span>
                )}
                <span className="flex-1" />
                <span
                  className={cn(
                    "flex-none font-mono text-[10px] font-semibold",
                    isTrue ? "text-green-600" : "text-red-600"
                  )}
                >
                  {isTrue ? "TRUE" : "FALSE"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {unknown.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand-muted">
            Unknown
          </h3>
          {unknown.map((item) => (
            <div
              key={item.question}
              className={cn(
                "flex items-center gap-2 rounded-md border bg-brand-background px-2.5 py-1.5",
                item.isAskingNext
                  ? "border-unlikely/30"
                  : item.hasConflict
                    ? "border-yellow-500/35"
                    : "border-brand-border"
              )}
            >
              {item.hasConflict ? (
                <AlertTriangle
                  className="h-3.5 w-3.5 flex-none text-amber-700"
                  aria-hidden={true}
                />
              ) : (
                <HelpCircle
                  className="h-3.5 w-3.5 flex-none text-amber-600"
                  aria-hidden={true}
                />
              )}
              <span className="text-xs text-brand-muted">{item.question}</span>
              <span className="flex-1" />
              {item.hasConflict && (
                <span className="flex-none rounded-full bg-yellow-500/[0.12] px-1.5 py-px text-[10px] font-semibold text-amber-700">
                  answers conflict
                </span>
              )}
              {item.isAskingNext && (
                <span className="flex-none rounded-full border border-unlikely/30 bg-unlikely/[0.09] px-1.5 py-px text-[10px] font-semibold text-unlikely">
                  asking next
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </CollapsibleCard>
  );
}
